import { useState, useEffect } from "react";
import axios from "axios";
import { Bell, Calendar, Plane, Mail, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => `₹${(value || 0).toLocaleString()}`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const AdminReminders = () => {
  const { token } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remindLoadingId, setRemindLoadingId] = useState(null);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API}/admin/reminders/payment`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminders(response.data);
    } catch (error) {
      console.error("Error fetching reminders:", error);
      toast.error("Failed to load reminders");
    } finally {
      setLoading(false);
    }
  };

  const sendEmailReminder = async (booking) => {
    if (!booking?.agent_email) {
      toast.error("Agent email not available for this booking");
      return;
    }
    setRemindLoadingId(`email-${booking.booking_id}`);
    try {
      const res = await axios.post(
        `${API}/bookings/${booking.booking_id}/send-payment-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reminder emailed to ${res.data?.to || booking.agent_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder email");
    } finally {
      setRemindLoadingId(null);
    }
  };

  const sendWhatsAppReminder = (booking) => {
    const phone = (booking.agent_phone || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("Agent phone number not available");
      return;
    }
    const bookingAmt = formatCurrency(booking.total_amount);
    const recdAmt = formatCurrency(booking.amount_paid);
    const balAmt = formatCurrency(booking.pending_amount);
    const msg =
      `Hi ${booking.agent_name || "there"}, this is a friendly payment reminder from Happy Holidays DMC for booking #${booking.booking_ref} (${booking.destination}, travel date ${formatDate(booking.travel_date)}).%0A%0A` +
      `Booking Amount: ${bookingAmt}%0A` +
      `Received Amount: ${recdAmt}%0A` +
      `Balance Amount: ${balAmt}%0A%0A` +
      `Kindly settle the pending balance at your earliest convenience. Thank you!`;
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
    // Log follow-up (best-effort; ignore errors)
    axios.post(
      `${API}/bookings/${booking.booking_id}/followups`,
      { type: "whatsapp", sent_to: booking.agent_phone || "", note: `WhatsApp reminder · Balance ${balAmt}` },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {});
  };

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="admin-reminders">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Payment Reminders</h1>
          <p className="text-gray-500">Follow up on pending payments for upcoming trips (Next 7 Days)</p>
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-gray-900 flex items-center gap-2 text-lg">
              <Bell className="w-5 h-5 text-emerald-600" />
              Pending Collections
              <span className="text-sm font-normal text-gray-500">(7 days)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
              </div>
            ) : reminders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="reminders-bookings-table">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Travel Date</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking ID</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Agent Company / User</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nights / Destination</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Guest</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking Amt</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Recd Amt</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance Amt</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {reminders.map((booking, index) => (
                      <tr key={booking.booking_id || index} className="hover:bg-gray-50 transition-colors" data-testid={`reminder-row-${index}`}>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-800">{formatDate(booking.travel_date)}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="font-mono text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded text-xs">
                            #{booking.booking_ref}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-gray-800 font-medium">{booking.company_name || '-'}</div>
                          <div className="text-xs text-gray-500">{booking.agent_name}</div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-gray-800 font-medium">{booking.nights || 0}N {booking.destination}</div>
                        </td>
                        <td className="px-3 py-3">
                          <span className="text-gray-800">{booking.guest_name}</span>
                        </td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <span className="text-gray-800 font-semibold">{formatCurrency(booking.total_amount)}</span>
                        </td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <span className="text-emerald-700 font-semibold">{formatCurrency(booking.amount_paid)}</span>
                        </td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <span className={`font-semibold px-2 py-1 rounded ${
                            booking.pending_amount > 0
                              ? "text-red-600 bg-red-50"
                              : "text-emerald-600 bg-emerald-50"
                          }`}>
                            {formatCurrency(booking.pending_amount)}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => sendEmailReminder(booking)}
                              disabled={remindLoadingId === `email-${booking.booking_id}` || !booking.agent_email}
                              title={booking.agent_email ? `Send payment reminder to ${booking.agent_email}` : "Agent email unavailable"}
                              className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              data-testid={`reminder-email-btn-${index}`}
                            >
                              {remindLoadingId === `email-${booking.booking_id}` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Mail className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => sendWhatsAppReminder(booking)}
                              disabled={!booking.agent_phone}
                              title={booking.agent_phone ? `WhatsApp ${booking.agent_phone}` : "Agent phone unavailable"}
                              className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              data-testid={`reminder-whatsapp-btn-${index}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No upcoming trips with pending payments in the next 7 days</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminReminders;
